import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import ComprehensiveAnalysisResults from '@/components/ComprehensiveAnalysisResults';
import AIEnhancement from '@/components/AIEnhancement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Share = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSharedAnalysis();
  }, [shareId]);

  const fetchSharedAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/share/${shareId}`);
      setAnalysis(response.data);
    } catch (error) {
      console.error('Error fetching shared analysis:', error);
      setError('Analysis not found or has expired');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading analysis...</p>
        </div>
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <p className="text-red-600 mb-4">{error || 'Analysis not found'}</p>
          <button 
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{analysis.dominant_emotion} Analysis | EmoQ</title>
        <meta name="description" content={`Emotional analysis: ${analysis.dominant_emotion} detected with ${Math.round(analysis.valence * 100)}% valence. View detailed insights on EmoQ`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:title" content={`${analysis.dominant_emotion} Analysis | EmoQ`} />
        <meta property="og:description" content={`Emotional analysis results: ${analysis.dominant_emotion} detected. ${analysis.content_description || 'View detailed emotional intelligence insights'}`} />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_credit-system-12/artifacts/dm8oae4k_Emo.zip%20-%205.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${analysis.dominant_emotion} Analysis | EmoQ`} />
        <meta name="twitter:description" content={`Emotional analysis: ${analysis.dominant_emotion} detected`} />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_credit-system-12/artifacts/dm8oae4k_Emo.zip%20-%205.png" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <header className="border-b border-blue-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-2 md:py-3 flex justify-between items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_credit-system-12/artifacts/3r5buf5n_Emo.zip%20-%204.png"
              alt="EmoQ"
              className="h-14 md:h-16 w-auto cursor-pointer"
              onClick={() => navigate('/')}
            />
            <button 
              onClick={() => navigate('/')}
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700"
            >
              Try EmoQ.ai
            </button>
          </div>
        </header>

        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Shared by:</span> {analysis.user_name || 'Anonymous User'}
              {' • '}
              <span className="text-gray-500">{analysis.view_count || 0} views</span>
            </p>
          </div>

          {/* Content Description */}
          {analysis.content_description && (
            <Card className="bg-blue-50 border-blue-200 mb-6">
              <CardContent className="pt-6">
                <Label className="text-sm font-semibold mb-2 block text-blue-900">Content Analysis</Label>
                <p className="text-base text-gray-700">{analysis.content_description}</p>
              </CardContent>
            </Card>
          )}

          {/* Analyzed Content/Image */}
          {analysis.image_data && (
            <Card className="border-blue-100 mb-6">
              <CardContent className="pt-6">
                <Label className="text-sm mb-2 block text-gray-700">Analyzed Content</Label>
                <img src={analysis.image_data} alt="Analyzed" className="w-full max-h-96 object-contain rounded" />
              </CardContent>
            </Card>
          )}

          {/* Comprehensive Analysis Results */}
          <ComprehensiveAnalysisResults result={analysis} darkMode={false} />

          {/* AI Enhancement Section if available */}
          {analysis.enhancement && (
            <div className="mt-8">
              <AIEnhancement analysis={analysis} darkMode={false} />
            </div>
          )}

          <div className="mt-8 text-center p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
            <h3 className="text-xl font-bold mb-3 text-gray-900">Want to analyze your own content?</h3>
            <p className="text-sm mb-4 text-gray-600">
              Try EmoQ.ai's powerful emotional intelligence analysis
            </p>
            <button 
              onClick={() => navigate('/')}
              className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 font-semibold"
            >
              Try EmoQ.ai Free
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Share;
